import React, { useState, useMemo } from 'react';
import { DynamicFlapperLogo } from './index';
import { useToast } from '../contexts/ToastContext';
import './MatchCompletionMoment.css';

/**
 * Full-screen takeover shown after a match goes FINAL. Highest-emotion
 * beat in the app: the user just finished scoring a real match, and
 * we want to give them a moment of payoff + put sharing one tap away.
 *
 * Layout, top to bottom:
 *   "MATCH FINAL" eyebrow
 *   Dynamic logo (the moment — face reacts to outcome)
 *   Winner team name
 *   Set-by-set scoreline (volleyball convention — numbers only)
 *   Share / Done
 *
 * Props:
 *   - match: finalized match ({ teamA, teamB, score, setsWon, sets, ... })
 *   - viewerUrl: public read-only URL for sharing
 *   - onDone: dismiss handler
 */
function MatchCompletionMoment({ match, viewerUrl, onDone }) {
  const toast = useToast();
  const [sharing, setSharing] = useState(false);

  if (!match) return null;

  const teamA = match.teamA || {};
  const teamB = match.teamB || {};
  const setsWon = match.setsWon || { teamA: 0, teamB: 0 };

  const aWon = setsWon.teamA > setsWon.teamB;
  const bWon = setsWon.teamB > setsWon.teamA;
  const winnerName = aWon
    ? (teamA.name || 'Team A')
    : bWon
      ? (teamB.name || 'Team B')
      : null;
  // Volleyball convention: match score = sets won, winner first.
  // "Tyrants wins 2–1" not "Tyrants wins 25-18, 25-22, 23-25".
  // Per-set breakdown is detail for the match viewer page, not the
  // moment — the moment celebrates the result.
  const winnerSets = aWon ? setsWon.teamA : bWon ? setsWon.teamB : null;
  const loserSets = aWon ? setsWon.teamB : bWon ? setsWon.teamA : null;

  // Face: use the real DynamicFlapperLogo (so the visual treatment —
  // bevel, seam, noise, outline — stays consistent with every other
  // surface). But the emotion engine doesn't reliably produce a
  // celebratory face for a 1-set "casual" match (checkMatchCompletion
  // requires best-of-3+ to count a win). So we feed it *synthetic*
  // match data shaped to produce the emotion we want:
  //
  //   - 3 synthetic matches of "my team wins 2–0"  → winRate=1.0 → JOYFUL
  //   - 3 synthetic matches of "my team loses 0–2" → winRate=0.0 → COMPETITIVE
  //
  // "My team" heuristic: with only one match's data we can't know
  // whose perspective this is from. If exactly one side has a
  // customized (non-default) name, that side is most likely the
  // scorer's. Otherwise fall back to celebrating the winner.
  const DEFAULT_COLOR = '#C4B5A0';
  const isPlaceholderName = (name) => {
    const n = (name || '').trim();
    return !n || /^Team [A-Z]$/i.test(n) || /^Team \d+$/i.test(n);
  };
  const aCustomized = !isPlaceholderName(teamA.name);
  const bCustomized = !isPlaceholderName(teamB.name);
  let myTeamSide = null;
  if (aCustomized && !bCustomized) myTeamSide = 'A';
  else if (!aCustomized && bCustomized) myTeamSide = 'B';

  // Decide whose perspective the moment celebrates.
  // celebrantSide: the side we'll center on (paint with their color)
  // celebrantWon:  did that side win the match?
  let celebrantSide = myTeamSide;
  if (!celebrantSide && winnerName) celebrantSide = aWon ? 'A' : 'B';
  const celebrantWon = celebrantSide
    ? (celebrantSide === 'A' ? aWon : bWon)
    : null;

  const syntheticMatches = useMemo(() => {
    if (!celebrantSide) return []; // tie / no winner → engine defaults to neutral
    const celebrantName = celebrantSide === 'A' ? teamA.name : teamB.name;
    const celebrantColor = (celebrantSide === 'A' ? teamA.color : teamB.color) || DEFAULT_COLOR;
    const opName = celebrantSide === 'A' ? teamB.name : teamA.name;
    const opColor = (celebrantSide === 'A' ? teamB.color : teamA.color) || DEFAULT_COLOR;

    // The engine's inferMyTeam filters placeholder names ("Team A",
    // etc.) so we substitute a synthetic non-placeholder when needed.
    // Engine only sees this in the synthetic data — UI never displays it.
    const myEngineName = isPlaceholderName(celebrantName) ? 'My Team' : celebrantName;
    const opEngineName = isPlaceholderName(opName) ? 'Opponent' : opName;

    // 3 matches gets us into the engine's 3-7 "basic" bucket where
    // winRate≥0.6 = JOYFUL, ≥0.4 = CALM, else COMPETITIVE.
    const winSets = [{ teamA: 25, teamB: 22 }, { teamA: 25, teamB: 23 }];
    const lossSets = [{ teamA: 22, teamB: 25 }, { teamA: 23, teamB: 25 }];
    const makeMatch = (i) => ({
      id: `__moment_synth_${i}`,
      teamA: { name: myEngineName, color: celebrantColor },
      teamB: { name: opEngineName, color: opColor },
      sets: celebrantWon ? winSets : lossSets,
      setsWon: celebrantWon ? { teamA: 2, teamB: 0 } : { teamA: 0, teamB: 2 },
      score: celebrantWon ? { teamA: 25, teamB: 23 } : { teamA: 23, teamB: 25 },
      status: 'final',
      createdAt: Date.now(),
    });
    return [makeMatch(0), makeMatch(1), makeMatch(2)];
  }, [celebrantSide, celebrantWon, teamA.name, teamA.color, teamB.name, teamB.color]);

  const handleShare = async () => {
    if (sharing) return;
    setSharing(true);
    const shareData = {
      title: `${teamA.name || 'Team A'} vs ${teamB.name || 'Team B'}`,
      text: winnerName
        ? `${winnerName} just won — ${setsWon.teamA}-${setsWon.teamB}`
        : `${teamA.name || 'Team A'} vs ${teamB.name || 'Team B'} — final`,
      url: viewerUrl,
    };
    try {
      if (typeof navigator !== 'undefined' && navigator.share) {
        await navigator.share(shareData);
      } else if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(viewerUrl);
        toast.success('Link copied');
      } else {
        toast.info(viewerUrl);
      }
    } catch (err) {
      if (err && err.name !== 'AbortError') {
        toast.error('Couldn’t share');
      }
    } finally {
      setSharing(false);
    }
  };

  return (
    <div className="match-completion" role="dialog" aria-modal="true">
      <div className="match-completion-inner">
        <p className="match-completion-eyebrow">Match final</p>

        <div className="match-completion-logo">
          <DynamicFlapperLogo
            matches={syntheticMatches}
            size="large"
            showTooltip={false}
          />
        </div>

        <h1 className="match-completion-winner">
          {winnerName || `${teamA.name || 'Team A'} vs ${teamB.name || 'Team B'}`}
        </h1>

        {winnerSets !== null && (
          <p className="match-completion-score">
            <span className="match-completion-score-win">{winnerSets}</span>
            <span className="match-completion-score-sep" aria-hidden="true">–</span>
            <span className="match-completion-score-lose">{loserSets}</span>
          </p>
        )}

        <div className="match-completion-actions">
          <button
            type="button"
            className="match-completion-share"
            onClick={handleShare}
            disabled={sharing}
          >
            {sharing ? 'Sharing…' : 'Share match'}
          </button>
          <button
            type="button"
            className="match-completion-done"
            onClick={onDone}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

export default MatchCompletionMoment;
